import { Injectable } from '@angular/core';
import {
  AuthHttpService,
  ChatHttpService,
} from '@app/core/async-services/http';
import {
  AuthService,
  ClearStateService,
  PubNubService,
} from '@app/core/services';
import { NavController } from '@ionic/angular';
import { EMPTY, of } from 'rxjs';
import { catchError, filter, first, map, switchMap, tap } from 'rxjs/operators';
import * as AuthAction from './auth.actions';
import { PendoService } from '@app/core/services/pendo.service';
import { PushNotificationsService } from '@app/core/services/push-notifications/push-notifications.service';
import { Store } from '@ngrx/store';
import { State } from '../store.state';
import { selectToken } from '../pubnub';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { StorageService } from '@app/core/storage/storage.service';

@Injectable()
export class Effects {
  clearSessionCookies$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(
          AuthAction.sessionExpired,
          AuthAction.lostNetworkConnection,
          AuthAction.logoutRequestSucceeded,
        ),
        tap(async () => await this._authService.clearAllSessionCookies()),
      ),
    { dispatch: false },
  );

  clearServiceStates$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(
          AuthAction.logoutRequestSucceeded,
          AuthAction.sessionExpired,
          AuthAction.lostNetworkConnection,
        ),
        tap(() => this._clearStateService.clearState()),
        tap(() => this._pendoService.clearSession()),
        tap(() => this.storageService.clearCsrfToken()),
      ),
    { dispatch: false },
  );

  unregisterPushNotifications$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(AuthAction.logoutRequestSucceeded, AuthAction.sessionExpired),
        tap(() => this.pushNotificationsService.unregister()),
      ),
    { dispatch: false },
  );

  navigateToSignOut$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(
          AuthAction.logoutRequestSucceeded,
          AuthAction.sessionExpired,
          AuthAction.lostNetworkConnection,
        ),
        tap(({ type }) => {
          let queryParams: SignOutPageParams;

          if (type === '[Auth] Session Expired') {
            queryParams = sessionExpiredParams;
          } else if (type === '[Auth] Lost Network Connection') {
            queryParams = lostNetworkConnectionParams;
          } else {
            queryParams = defaultSignOutParams;
          }

          this._navController.navigateRoot('signout', { queryParams });
        }),
      ),
    { dispatch: false },
  );

  revokeChannelPermissions$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthAction.signOut),
      switchMap(() => this._authService.isAuthenticated()),
      filter((isAuthenticated) => isAuthenticated),
      switchMap(() => this.store.select(selectToken).pipe(first())),
      switchMap((token) => {
        if (!token) {
          return of(AuthAction.revokeChannelPermissionsSuccess());
        }
        return this._chatHttpService.revokeToken(token).pipe(
          map(() => AuthAction.revokeChannelPermissionsSuccess()),
          catchError(() => of(AuthAction.revokeChannelPermissionsFailure())),
        );
      }),
    ),
  );

  requestLogout$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        AuthAction.revokeChannelPermissionsSuccess,
        AuthAction.revokeChannelPermissionsFailure,
      ),
      switchMap(() =>
        this._authHttpService.logout().pipe(
          map(() => AuthAction.logoutRequestSucceeded()),
          catchError(() => EMPTY),
        ),
      ),
    ),
  );

  stopPubnub$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(
          AuthAction.revokeChannelPermissionsSuccess,
          AuthAction.revokeChannelPermissionsFailure,
          AuthAction.lostNetworkConnection,
        ),
        switchMap(() => this._pubNubService.isInitialized$),
        filter((isInitialized) => isInitialized),
        tap(() => {
          this._pubNubService.stop();
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private _actions$: Actions,
    private _authHttpService: AuthHttpService,
    private _authService: AuthService,
    private _clearStateService: ClearStateService,
    private _navController: NavController,
    private _pubNubService: PubNubService,
    private _chatHttpService: ChatHttpService,
    private _pendoService: PendoService,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    private pushNotificationsService: PushNotificationsService,
    private store: Store<State>,
    private storageService: StorageService,
  ) {}
}

interface SignOutPageParams {
  title_id: string;
  message_id: string;
}

const defaultSignOutParams: Readonly<SignOutPageParams> = {
  title_id: 'AUTH_LOGOUT_TITLE',
  message_id: 'AUTH_LOGOUT_MESSAGE',
};

const sessionExpiredParams: Readonly<SignOutPageParams> = {
  title_id: 'AUTH_SESSION_EXPIRED_TITLE',
  message_id: 'AUTH_SESSION_EXPIRED_MESSAGE',
};

const lostNetworkConnectionParams: Readonly<SignOutPageParams> = {
  title_id: 'AUTH_LOGOUT_TITLE',
  message_id: 'AUTH_LOGOUT_NETWORK_LOST_MESSAGE',
};
